import { useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, type MeResponse } from '../game/api';
import { audio } from '../game/audio';
import { formatDate, formatNumber, t, type Key } from '../i18n';
import { missionLabel, seasonName } from '../i18n/catalog';
import { Amount, GemIcon, Icon } from './icons';
import { PageHead, type Page } from './MenuShell';
import { RewardArt, isMajor, rewardKindLabel, rewardLabel, type PassLevel, type Reward } from './rewards';

const XP_PER_LEVEL = 2000;
const MAX_LEVEL = 105;
const PREMIUM_GEMS = 900;

let levelsCache: PassLevel[] | null = null;
let levelsReq: Promise<PassLevel[]> | null = null;

/** Tableau des 105 niveaux (public, identique pour tous), chargé une fois par session. */
export function usePassLevels(): PassLevel[] | null {
  const [levels, setLevels] = useState(levelsCache);
  useEffect(() => {
    if (levelsCache) return;
    levelsReq ??= apiGet<{ levels: PassLevel[] }>('/api/pass').then((r) => (levelsCache = r.levels));
    let live = true;
    levelsReq
      .then((l) => live && setLevels(l))
      .catch(() => {
        levelsReq = null;
      });
    return () => {
      live = false;
    };
  }, []);
  return levels;
}

type Track = 'free' | 'premium';
const seenKey = (id: string) => `tankshift.passSeen.${id}`;

function readSeen(id: string): number | null {
  try {
    const v = localStorage.getItem(seenKey(id));
    return v === null ? null : Number(v);
  } catch {
    return null;
  }
}

function writeSeen(id: string, level: number): void {
  try {
    localStorage.setItem(seenKey(id), String(level));
  } catch {
    /* stockage indisponible : la révélation sera rejouée une fois */
  }
}

/** Libellé court sous la tuile : montant, multiplicateur ou nom de l'objet. */
function shortLabel(r: Reward): string {
  if (r.kind === 'coins' || r.kind === 'gems') return formatNumber(r.amount);
  if (r.kind === 'token') return r.amount > 1 ? `×${r.amount}` : t(`tokenShort_${r.token}` as Key);
  return rewardLabel(r);
}

export function PassPage({ me, onChanged, go }: { me: MeResponse | null; onChanged: () => void; go: (p: Page) => void }) {
  const levels = usePassLevels();
  const pass = me?.pass;
  const account = me?.account;
  const level = pass?.level ?? 0;
  const premium = !!pass?.premium;
  const claimed = useMemo(() => new Set(pass?.claimed ?? []), [pass?.claimed]);
  const inLevel = (pass?.xp ?? 0) % XP_PER_LEVEL;
  const [msg, setMsg] = useState('');
  const [buying, setBuying] = useState(false);
  const [sel, setSel] = useState<{ level: number; track: Track } | null>(null);
  const [reveal, setReveal] = useState<{ from: number; to: number } | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  // Sélection par défaut : prochaine récompense marquante devant le joueur.
  const current = sel ?? (() => {
    const next = levels?.find((l) => l.level > level && (isMajor(l.free) || isMajor(l.premium)));
    return next ? { level: next.level, track: (isMajor(next.free) ? 'free' : 'premium') as Track } : { level: Math.max(1, level), track: 'free' as Track };
  })();
  const selLevel = levels?.find((l) => l.level === current.level);

  // Paliers franchis depuis la dernière visite : petite révélation, une seule fois.
  useEffect(() => {
    if (!account || !pass) return;
    const seen = readSeen(account.publicId);
    if (seen !== null && pass.level > seen) {
      setReveal({ from: seen + 1, to: pass.level });
      audio.play('uiReward');
    }
    writeSeen(account.publicId, pass.level);
  }, [account?.publicId, pass?.level]);

  // Ouverture : la piste se place sur le niveau atteint.
  useEffect(() => {
    if (!levels) return;
    const el = trackRef.current?.querySelector<HTMLElement>(`[data-level="${Math.max(1, level)}"]`);
    el?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [levels]);

  // Molette verticale → défilement horizontal de la piste ; glisser à la souris pour la parcourir.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const max = el.scrollWidth - el.clientWidth;
      const pos = Math.abs(el.scrollLeft);
      if ((e.deltaY < 0 && pos <= 0) || (e.deltaY > 0 && pos >= max - 1)) return;
      e.preventDefault();
      el.scrollLeft += getComputedStyle(el).direction === 'rtl' ? -e.deltaY : e.deltaY;
    };
    let drag: { x: number; left: number; moved: boolean } | null = null;
    const down = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: el.scrollLeft, moved: false };
    };
    const move = (e: PointerEvent) => {
      if (!drag) return;
      const dx = e.clientX - drag.x;
      if (Math.abs(dx) > 5) drag.moved = true;
      if (drag.moved) {
        el.scrollLeft = drag.left - dx;
        el.classList.add('dragging');
      }
    };
    const up = () => {
      if (drag?.moved) {
        // Le relâchement après un glisser ne doit pas sélectionner la tuile sous le pointeur.
        const stop = (ev: Event) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        el.addEventListener('click', stop, { capture: true, once: true });
        setTimeout(() => el.removeEventListener('click', stop, { capture: true }), 0);
      }
      drag = null;
      el.classList.remove('dragging');
    };
    el.addEventListener('wheel', wheel, { passive: false });
    el.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      el.removeEventListener('wheel', wheel);
      el.removeEventListener('pointerdown', down);
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [levels]);

  const page = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (!el) return;
    const rtl = getComputedStyle(el).direction === 'rtl';
    el.scrollBy({ left: dir * (rtl ? -1 : 1) * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  const focusTile = (lv: number, tr: Track) => {
    setSel({ level: lv, track: tr });
    requestAnimationFrame(() => trackRef.current?.querySelector<HTMLElement>(`[data-tile="${lv}-${tr}"]`)?.focus());
  };
  const onTileKey = (e: React.KeyboardEvent, lv: number, tr: Track) => {
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
    const fwd = rtl ? 'ArrowLeft' : 'ArrowRight';
    const back = rtl ? 'ArrowRight' : 'ArrowLeft';
    if (e.key === fwd && lv < MAX_LEVEL) focusTile(lv + 1, tr);
    else if (e.key === back && lv > 1) focusTile(lv - 1, tr);
    else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') focusTile(lv, tr === 'free' ? 'premium' : 'free');
    else if (e.key === 'Home') focusTile(1, tr);
    else if (e.key === 'End') focusTile(MAX_LEVEL, tr);
    else return;
    e.preventDefault();
  };

  const buyPremium = async () => {
    setBuying(true);
    const r = await apiPost<{ result?: string; error?: string }>('/api/pass/premium');
    setBuying(false);
    const key = r.data.result ?? r.data.error ?? 'server';
    setMsg(t(`premium_${key}` as Key));
    audio.play(key === 'ok' ? 'uiBuy' : 'uiDeny');
    onChanged();
  };

  const status = (lv: number, tr: Track): 'claimed' | 'reached' | 'locked' | 'ahead' => {
    if (claimed.has(`${lv}:${tr}`)) return 'claimed';
    if (tr === 'premium' && !premium) return 'locked';
    return lv <= level ? 'reached' : 'ahead';
  };
  const statusText = (s: ReturnType<typeof status>, lv: number) =>
    s === 'claimed' ? t('passClaimed') : s === 'locked' ? t('passPremiumNeeded') : s === 'reached' ? t('passReached') : t('passIn', { n: lv - level });

  const season = pass?.season ?? { id: 'S1', name: 'GENÈSE MÉCANIQUE', endsAt: 0 };
  const selReward = selLevel ? selLevel[current.track] : null;
  const selStatus = selLevel ? status(selLevel.level, current.track) : null;
  const gems = me?.balances.gems ?? 0;

  return (
    <div className="page pass-page">
      <PageHead
        title={t('passTitle')}
        sub={
          <>
            <span className="display">{seasonName(season.id, season.name)}</span>
            {pass && <> · {pass.ended ? t('passEnded') : t('passEnds', { d: formatDate(pass.season.endsAt) })}</>}
          </>
        }
      />

      {reveal && levels && (
        <section className="plate pass-reveal" role="status">
          <span className="stamp display">{t('passNewTitle')}</span>
          <div className="pass-reveal-body">
            <p>{reveal.from === reveal.to ? t('passNewOne', { n: reveal.to }) : t('passNewMany', { a: reveal.from, b: reveal.to })}</p>
            <div className="pass-reveal-list">
              {levels
                .filter((l) => l.level >= reveal.from && l.level <= reveal.to)
                .flatMap((l) => [l.free, ...(premium ? [l.premium] : [])].map((r, i) => ({ r, key: `${l.level}-${i}` })))
                .slice(0, 12)
                .map(({ r, key }) => (
                  <span key={key} className="reveal-item" title={rewardLabel(r)}>
                    <RewardArt r={r} size={40} />
                  </span>
                ))}
            </div>
          </div>
          <button type="button" className="icon-btn" aria-label={t('close')} onClick={() => setReveal(null)}>
            <Icon name="close" size={16} />
          </button>
        </section>
      )}

      <section className="pass-hero">
        <div className="plate pass-progress">
          <div className="pass-badge" aria-hidden>
            <span className="display num">{Math.max(1, level)}</span>
          </div>
          <div className="pass-progress-body">
            <span className="eyebrow">{account ? t('passYourLevel') : t('passPreview')}</span>
            <strong className="pass-level-line display num">{account ? t('passLevelOf', { n: Math.max(1, level), max: MAX_LEVEL }) : t('passLevels', { n: MAX_LEVEL })}</strong>
            {pass ? (
              <>
                <div className="bar bar-xp">
                  <div className="fill fill-xp" style={{ width: `${level >= MAX_LEVEL ? 100 : (inLevel / XP_PER_LEVEL) * 100}%` }} />
                  <span>{level >= MAX_LEVEL ? t('evolveMax') : `${formatNumber(inLevel)} / ${formatNumber(XP_PER_LEVEL)} XP`}</span>
                </div>
                <p className="wip">{t('passXpInfo')}</p>
              </>
            ) : (
              <>
                <p className="wip">{t('passGuestInfo')}</p>
                <button type="button" className="btn btn-small" onClick={() => go('profile')}>
                  <Icon name="profile" size={16} />
                  {t('register')}
                </button>
              </>
            )}
          </div>
        </div>
        <div className={`plate pass-premium ${premium ? 'owned' : ''}`}>
          <span className="eyebrow">{t('passPremium')}</span>
          {premium ? (
            <>
              <strong className="display premium-on">
                <Icon name="star" size={18} />
                {t('passPremiumActive')}
              </strong>
              <p className="wip">{t('passPremiumActiveInfo')}</p>
            </>
          ) : (
            <>
              <strong className="premium-price">
                <GemIcon size={22} />
                <span className="display num">{formatNumber(PREMIUM_GEMS)}</span>
              </strong>
              <p className="wip">{t('premiumInfo')}</p>
              {account && (
                <p className="wip num">
                  {t('passBalance')} <Amount kind="gems" n={gems} size={14} />
                </p>
              )}
              <button type="button" className="btn btn-primary" disabled={!account || buying || pass?.ended} onClick={() => void buyPremium()}>
                {t('buyPremium')}
              </button>
            </>
          )}
          {msg && <p className="form-msg">{msg}</p>}
        </div>
      </section>

      <section className="pass-board" aria-label={t('passTrackLabel')}>
        <div className="pass-rails" aria-hidden>
          <span className="pass-rail-label">{t('passFree')}</span>
          <span className="pass-rail-label premium">
            <Icon name="star" size={14} />
            {t('passPremium')}
          </span>
        </div>
        <button type="button" className="icon-btn track-nav prev" aria-label={t('passPrev')} onClick={() => page(-1)}>
          <Icon name="left" />
        </button>
        <div className="pass-track" ref={trackRef}>
          {!levels && <p className="wip">{t('loading')}</p>}
          {levels?.map((l) => {
            const reached = l.level <= level;
            const milestone = l.level % 5 === 0 || isMajor(l.free) || isMajor(l.premium);
            return (
              <div key={l.level} data-level={l.level} className={`pass-level ${reached ? 'reached' : ''} ${l.level === level ? 'current' : ''} ${milestone ? 'milestone' : ''}`}>
                <span className="pass-num num">{l.level === level ? <span className="pass-here">{t('passHere')}</span> : null}{l.level}</span>
                {(['free', 'premium'] as Track[]).map((tr) => {
                  const r = l[tr];
                  const s = status(l.level, tr);
                  const on = current.level === l.level && current.track === tr;
                  return (
                    <button
                      key={tr}
                      type="button"
                      data-tile={`${l.level}-${tr}`}
                      className={`pass-tile ${tr} ${s} ${isMajor(r) ? 'major' : ''} ${on ? 'on' : ''}`}
                      aria-pressed={on}
                      tabIndex={on ? 0 : -1}
                      aria-label={`${t('level', { n: l.level })} · ${tr === 'free' ? t('passFree') : t('passPremium')} : ${rewardLabel(r)} · ${statusText(s, l.level)}`}
                      onClick={() => setSel({ level: l.level, track: tr })}
                      onKeyDown={(e) => onTileKey(e, l.level, tr)}
                    >
                      <RewardArt r={r} size={isMajor(r) ? 46 : 34} />
                      <span className="pass-tile-label">{shortLabel(r)}</span>
                      {s === 'claimed' && (
                        <span className="pass-state ok" aria-hidden>
                          <Icon name="check" size={14} />
                        </span>
                      )}
                      {s === 'locked' && (
                        <span className="pass-state" aria-hidden>
                          <Icon name="lock" size={13} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
        <button type="button" className="icon-btn track-nav next" aria-label={t('passNext')} onClick={() => page(1)}>
          <Icon name="right" />
        </button>
      </section>

      <div className="pass-lower">
        {selLevel && selReward && selStatus && (
          <section className={`plate pass-detail ${current.track}`} aria-live="polite">
            <div className="pass-detail-art">
              <RewardArt r={selReward} size={104} />
            </div>
            <div className="pass-detail-body">
              <span className="eyebrow">
                {t('level', { n: selLevel.level })} · {current.track === 'free' ? t('passFree') : t('passPremium')} · {rewardKindLabel(selReward)}
              </span>
              <h2 className="display">{rewardLabel(selReward)}</h2>
              <p className="wip">{selReward.kind === 'token' ? t(`tokdesc_${selReward.token}` as Key) : selReward.kind === 'item' ? t('rkdesc_item', { s: seasonName(season.id, season.name) }) : t(`rkdesc_${selReward.kind}` as Key)}</p>
              {account && (
                <span className={`badge ${selStatus === 'claimed' ? 'badge-ok' : selStatus === 'locked' ? 'badge-brass' : 'badge-accent'}`}>
                  <Icon name={selStatus === 'claimed' ? 'check' : selStatus === 'locked' ? 'lock' : 'pass'} size={12} />
                  {statusText(selStatus, selLevel.level)}
                </span>
              )}
            </div>
          </section>
        )}
        {me?.missions && (
          <section className="plate missions" aria-labelledby="missions-title">
            <h2 className="plate-title" id="missions-title">
              {t('passEarnTitle')}
            </h2>
            <h3 className="eyebrow">{t('dailyMissions', { c: me.missions.dailyReward.coins, x: me.missions.dailyReward.passXp })}</h3>
            {me.missions.daily.map((m) => (
              <MissionRow key={m.id} m={m} />
            ))}
            <h3 className="eyebrow">{t('weeklyMissions', { c: me.missions.weeklyReward.coins, x: me.missions.weeklyReward.passXp })}</h3>
            {me.missions.weekly.map((m) => (
              <MissionRow key={m.id} m={m} />
            ))}
          </section>
        )}
      </div>
    </div>
  );
}

export function MissionRow({ m }: { m: { id: string; label: string; target: number; progress: number; done: boolean } }) {
  return (
    <div className={`mission ${m.done ? 'done' : ''}`}>
      <span className="mission-check" aria-hidden>
        {m.done ? <Icon name="check" size={14} /> : null}
      </span>
      <span className="mission-label">{missionLabel(m.id, m.label)}</span>
      <div className="bar">
        <div className="fill fill-xp" style={{ width: `${Math.min(100, (m.progress / m.target) * 100)}%` }} />
        <span className="num">{m.done ? t('missionDone') : `${formatNumber(m.progress)} / ${formatNumber(m.target)}`}</span>
      </div>
    </div>
  );
}
